/* eslint-disable react-hooks/exhaustive-deps */
import * as THREE from 'three'
import React, { useMemo, useState } from 'react'
import { useThree } from '@react-three/fiber';

import { const_var, params } from '../../../../../../../redux/reducers/reducer';
import { controls } from '../../../../../../../redux/reducers/common/eventReducer';

import { CSG } from '../../../../../../../assets/libs/csg-lib';
import { delModel, extrudeSettings, roofPattern, textureAnisotropy } from '../../../../../../../utils/Functions';
import useMouseEvents from '../../../../../../../hooks/useMouseEvents';
import { MaterialInnerWall, MaterialOuterWallNew, MaterialWainScotWallNew } from '../../../../../CustomMaterial';

const WallModel = () => {
    const { gl } = useThree ();

    const [frontWallCloseState, setFrontWallCloseState] = useState(0);
    const [backWallCloseState, setBackWallCloseState] = useState(0);
    const [leftWallCloseState, setLeftWallCloseState] = useState(0);
    const [rightWallCloseState, setRightWallCloseState] = useState(0);

    const wallThickness = 0.005;
    const cbFrontBackWallOffset = 0.155;
    // const offsetWithDoorThickness = 0.127;

    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement != undefined && canvasElement[0]?.clientWidth > 1050 && !params.p_v_w) ? true : false

     

    const dutchDelModelFront = useMemo(() => {
        const freeSpace = 1;
        let cornerFreeSpaceW = (params.lean_p_w - freeSpace) / 6;
        const leftHeight = (params.singleSlope && params.freeStandingSide === "left") ? params.lean_p_h - ((params.b_l_t_r_p / 12) * params.lean_p_w) :  params.lean_p_h
        let cornerFreeSpaceH = (leftHeight - freeSpace) / 6
        if (cornerFreeSpaceH > freeSpace) cornerFreeSpaceH = freeSpace * 2;

        if (cornerFreeSpaceW > freeSpace) cornerFreeSpaceW = freeSpace * 2;
        
        if (params.add_left_lean && (params.p_b_c_b_l_f === "Half Dutch End" || params.p_b_c_b_l_f === "Half End")) {
            const model = new THREE.Shape();
            model.moveTo(-(params.lean_p_w / 2 - freeSpace), 0);

            if (params.p_b_c_b_l_f === "Half Dutch End") {
                model.lineTo(-(params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace - cornerFreeSpaceH);
                model.lineTo(-(params.lean_p_w / 2 - freeSpace - cornerFreeSpaceW), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace - cornerFreeSpaceW), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace - cornerFreeSpaceH);
            } else {
                model.lineTo(-(params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace);
            }
            model.lineTo((params.lean_p_w / 2 - freeSpace), 0);

            return model;
        } else {
            return []
        }
    }, [params.add_left_lean, params.lean_p_w, params.lean_p_h, params.p_b_c_b_l_f])

    const dutchDelModelBack = useMemo(() => {
        const freeSpace = 1;
        let cornerFreeSpaceW = (params.lean_p_w - freeSpace) / 6;
        const leftHeight = (params.singleSlope && params.freeStandingSide === "left") ? params.lean_p_h - ((params.b_l_t_r_p / 12) * params.lean_p_w) :  params.lean_p_h
        let cornerFreeSpaceH = (leftHeight - freeSpace) / 6
        if (cornerFreeSpaceH > freeSpace) cornerFreeSpaceH = freeSpace * 2;

        if (cornerFreeSpaceW > freeSpace) cornerFreeSpaceW = freeSpace * 2;
        
        if (params.add_left_lean && (params.p_b_c_b_l_b === "Half Dutch End" || params.p_b_c_b_l_b === "Half End")) {
            const model = new THREE.Shape();
            model.moveTo(-(params.lean_p_w / 2 - freeSpace), 0);

            if (params.p_b_c_b_l_b === "Half Dutch End") {
                model.lineTo(-(params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace - cornerFreeSpaceH);
                model.lineTo(-(params.lean_p_w / 2 - freeSpace - cornerFreeSpaceW), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace - cornerFreeSpaceW), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace - cornerFreeSpaceH);
            } else {
                model.lineTo(-(params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace);
            }
            model.lineTo((params.lean_p_w / 2 - freeSpace), 0);

            return model;
        } else {
            return []
        }
    }, [params.add_left_lean, params.lean_p_w, params.lean_p_h, params.p_b_c_b_l_b])
    
    const GableFrontWallModelObj = useMemo(() => {
        if(params.p_b_c_b_l_f === 'Open') return;
        
        let regularHeight;
        if(params.p_r_s === 1) regularHeight = const_var.roofRegularDst * 2;
        else regularHeight = 0

        let innerModel;
        let outerModel;
        let wainscotModel;

        // inner wall model
        const createModel = (cornerValue) => {
            const model = new THREE.Shape();
            
            if(params.p_r_s === 1){
                model.moveTo(-params.lean_p_w / 2, params.lean_p_h);
                model.lineTo(-params.lean_p_w / 2 + 0.01, params.lean_p_h + const_var.roofRegularDst);
                model.quadraticCurveTo(
                    -params.lean_p_w / 2 + 0.02, params.lean_p_h + const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.b_l_t_r_p / 12) / 10,
                    -params.lean_p_w / 2 + 0.01 + const_var.roofRegularDst, params.lean_p_h + const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.b_l_t_r_p / 12
                );
                model.lineTo(0, params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w/2)) + const_var.roofRegularDst * 2 - 0.03);
        
                model.lineTo(params.lean_p_w / 2 - 0.01 - const_var.roofRegularDst, params.lean_p_h + const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.b_l_t_r_p / 12);
                model.quadraticCurveTo(
                    params.lean_p_w / 2 - 0.01, params.lean_p_h + const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.b_l_t_r_p / 12) / 10,
                    params.lean_p_w / 2 - 0.01, params.lean_p_h + const_var.roofRegularDst
                );
                model.lineTo(params.lean_p_w / 2 - 0.01, params.lean_p_h);
                model.lineTo(params.lean_p_w / 2 + 0.005, cornerValue || 0);
                model.lineTo(-params.lean_p_w / 2 + 0.005, cornerValue || 0);
                model.closePath();
            } else{
                model.moveTo(-(params.lean_p_w / 2 + cbFrontBackWallOffset), cornerValue || 0);
                model.lineTo(-(params.lean_p_w / 2 + cbFrontBackWallOffset), params.lean_p_h + (params.p_r_s !== 1 && -0.02) + regularHeight);
                model.lineTo(0, params.lean_p_h + (params.b_l_t_r_p / 12) * (params.lean_p_w / 2 + 0.1) + regularHeight);
                model.lineTo((params.lean_p_w / 2 + cbFrontBackWallOffset), params.lean_p_h + (params.p_r_s !== 1 && -0.02) + regularHeight);
                model.lineTo((params.lean_p_w / 2 + cbFrontBackWallOffset), cornerValue || 0);
                model.closePath();
            }

            return model;
        }

        let innerWallPanelValue = (frontWallCloseState < 0) ? params.lean_p_h + regularHeight : frontWallCloseState;

        const innerWallModel = createModel(frontWallCloseState);
        const innerWallCutModel = createModel(innerWallPanelValue);

        const meshInnerWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(innerWallModel, extrudeSettings(0.005)));
        const meshDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('gableLeftLeanFront', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'red' }));
    
        let meshInnerWallModel_Temp = meshInnerWallModel.clone();
        meshInnerWallModel_Temp.rotateY(Math.PI / 2);
        meshInnerWallModel_Temp.position.set(-params.p_w / 2 - params.lean_p_d + 0.03, 0, 0);
        meshInnerWallModel_Temp.updateMatrixWorld();
    
        let meshDoorDelModel_Temp = meshDoorDelModel.clone();
        meshDoorDelModel_Temp.rotateY(Math.PI / 2)
        meshDoorDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_d -0.5, 0, -params.lean_p_pos);
        meshDoorDelModel_Temp.updateMatrixWorld();
    
        const meshInnerWallModel_CSG = CSG.fromMesh(meshInnerWallModel_Temp);
        const meshDoorDelModel_CSG = CSG.fromMesh(meshDoorDelModel_Temp);
    
        const innerWallModel_CSG = meshInnerWallModel_CSG.subtract(meshDoorDelModel_CSG);
        const CSGInnerWallModel = CSG.toMesh(
            innerWallModel_CSG,
            new THREE.Matrix4()
        )

        innerModel = CSGInnerWallModel.geometry;
    
        // outer wall model
        const horizontalDepth = params.lean_p_w + cbFrontBackWallOffset * 2
        const HSPosition = params.lean_p_w / -2 - cbFrontBackWallOffset
    
        const verticalHorizontalPosS = 
            {
                "x": params.p_w / -2 - params.lean_p_d,
                "y": (params.p_w_c_n && (params.p_b_c_b_l_f === 'Close') ? 2.935 : 0),
                "z": HSPosition
            }
        const verticalHorizontalRotS = 
            {
                "x": 0,
                "y": 0,
                "z": Math.PI / 2
            }
        const patternLengthS = params.lean_p_h + params.b_l_t_r_p / 12 * params.lean_p_w / 2 + regularHeight + (params.p_w_c_n && (params.p_b_c_b_l_f === 'Close') ? -2.935 : 0)
        const patternDepthS = horizontalDepth
        
        const meshOuterWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLengthS, isPattern), extrudeSettings(patternDepthS)));
        const meshOuterWallDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(innerWallCutModel, extrudeSettings(1)))
        
        let meshOuterWallModel_Temp = meshOuterWallModel.clone();
        meshOuterWallModel_Temp.position.set(verticalHorizontalPosS.x,verticalHorizontalPosS.y,verticalHorizontalPosS.z);
        meshOuterWallModel_Temp.rotation.set(verticalHorizontalRotS.x,verticalHorizontalRotS.y,verticalHorizontalRotS.z);
        meshOuterWallModel_Temp.updateMatrixWorld();
    
    
        let meshOuterWallDelModel_Temp = meshOuterWallDelModel.clone();
        meshOuterWallDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_d + 0.5 , 0, 0);
        meshOuterWallDelModel_Temp.rotation.set(0,Math.PI / -2,0)
        meshOuterWallDelModel_Temp.updateMatrixWorld();
    
        const meshOuterWallModel_CSG = CSG.fromMesh(meshOuterWallModel_Temp);
        const meshOuterWallDelModel_CSG = CSG.fromMesh(meshOuterWallDelModel_Temp);
    
        const outerWallModel_CSG = meshOuterWallModel_CSG.subtract(meshDoorDelModel_CSG);
        const meshOuterWallDel_CSG = outerWallModel_CSG.intersect(meshOuterWallDelModel_CSG);
        const CSGOuterWallModel = CSG.toMesh(meshOuterWallDel_CSG,new THREE.Matrix4());

        outerModel = CSGOuterWallModel.geometry;

        // wainscot wall model
        const wainscotVerticalHorizontalPosS = 
            {
                "x": params.p_w / -2 - params.lean_p_d,
                "y" : 0,
                "z" : HSPosition
            }
        const wainscotVerticalHorizontalRotS = 
            {
                "x": 0,
                "y": 0,
                "z": Math.PI / 2
            }
        const lengthPatternS = 3
        const depthPatternS = patternDepthS

        const meshWainscotWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPatternS, isPattern), extrudeSettings(depthPatternS)))
        let meshWainscotWallModel_Temp = meshWainscotWallModel.clone();
        meshWainscotWallModel_Temp.position.set(wainscotVerticalHorizontalPosS.x,wainscotVerticalHorizontalPosS.y,wainscotVerticalHorizontalPosS.z);
        meshWainscotWallModel_Temp.rotation.set(wainscotVerticalHorizontalRotS.x,wainscotVerticalHorizontalRotS.y,wainscotVerticalHorizontalRotS.z);
        meshWainscotWallModel_Temp.updateMatrixWorld();

        const meshWainscotWallModel_CSG = CSG.fromMesh(meshWainscotWallModel_Temp);

        const WainscotWallModel_CSG = meshWainscotWallModel_CSG.subtract(meshDoorDelModel_CSG);
        const WainscotWallModelCSG = WainscotWallModel_CSG.intersect(meshOuterWallDelModel_CSG);
        const CSGWainscotWallModel = CSG.toMesh(
            WainscotWallModelCSG,
            new THREE.Matrix4()
        )

        wainscotModel = CSGWainscotWallModel.geometry;

        return {innerModel, outerModel, wainscotModel}

    }, [params.wallTexture,params.lean_p_pos, params.p_v_w, params.p_w, params.b_l_t_r_p, params.lean_p_w, params.p_r_s, params.lean_p_d, params.lean_p_h, frontWallCloseState, params.add_storage_check, params.add_storage, const_var.newDoorsArray,params.p_b_c_b_l_f, params.p_w_c_n, isPattern]);

    const GableBackWallModelObj = useMemo(() => {
        if(params.p_b_c_b_l_b === 'Open') return;
        let regularHeight;
        if(params.p_r_s === 1) regularHeight = const_var.roofRegularDst * 2;
        else regularHeight = 0

        let innerModel;
        let outerModel;
        let wainscotModel;

        // inner wall model
        const createModel = (cornerValue) => {
            const model = new THREE.Shape();
            
            if(params.p_r_s === 1){
                model.moveTo(-params.lean_p_w / 2, params.lean_p_h);
                model.lineTo(-params.lean_p_w / 2 + 0.01, params.lean_p_h + const_var.roofRegularDst);
                model.quadraticCurveTo(
                    -params.lean_p_w / 2 + 0.02, params.lean_p_h + const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.b_l_t_r_p / 12) / 10,
                    -params.lean_p_w / 2 + 0.01 + const_var.roofRegularDst, params.lean_p_h + const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.b_l_t_r_p / 12
                );
                model.lineTo(0, params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w/2)) + const_var.roofRegularDst * 2 - 0.03);
        
                model.lineTo(params.lean_p_w / 2 - 0.01 - const_var.roofRegularDst, params.lean_p_h + const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.b_l_t_r_p / 12);
                model.quadraticCurveTo(
                    params.lean_p_w / 2 - 0.01, params.lean_p_h + const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.b_l_t_r_p / 12) / 10,
                    params.lean_p_w / 2 - 0.01, params.lean_p_h + const_var.roofRegularDst
                );
                model.lineTo(params.lean_p_w / 2 - 0.01, params.lean_p_h);
                model.lineTo(params.lean_p_w / 2 + 0.005, cornerValue || 0);
                model.lineTo(-params.lean_p_w / 2 + 0.005, cornerValue || 0);
                model.closePath();
            } else{
                model.moveTo(-(params.lean_p_w / 2 + cbFrontBackWallOffset), cornerValue || 0);
                model.lineTo(-(params.lean_p_w / 2 + cbFrontBackWallOffset), params.lean_p_h + (params.p_r_s !== 1 && -0.02) + regularHeight);
                model.lineTo(0, params.lean_p_h + (params.b_l_t_r_p / 12) * (params.lean_p_w / 2 + 0.1) + regularHeight);
                model.lineTo((params.lean_p_w / 2 + cbFrontBackWallOffset), params.lean_p_h + (params.p_r_s !== 1 && -0.02) + regularHeight);
                model.lineTo((params.lean_p_w / 2 + cbFrontBackWallOffset), cornerValue || 0);
                model.closePath();
            }

            return model;
        }

        let innerWallPanelValue = (backWallCloseState < 0) ? params.lean_p_h + regularHeight : backWallCloseState;

        const innerWallModel = createModel(backWallCloseState);
        const innerWallCutModel = createModel(innerWallPanelValue);

        const meshInnerWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(innerWallModel, extrudeSettings(0.005)));
        const meshDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('gableLeftLeanBack', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'red' }));
    
        let meshInnerWallModel_Temp = meshInnerWallModel.clone();
        meshInnerWallModel_Temp.rotateY(Math.PI / 2);
        meshInnerWallModel_Temp.position.set(-params.p_w / 2 - cbFrontBackWallOffset, 0, 0);
        meshInnerWallModel_Temp.updateMatrixWorld();

        let meshDoorDelModel_Temp = meshDoorDelModel.clone();
        meshDoorDelModel_Temp.rotateY(Math.PI / 2)
        meshDoorDelModel_Temp.position.set(-params.p_w / 2 -0.5, 0, -params.lean_p_pos);
        meshDoorDelModel_Temp.updateMatrixWorld();
    
        const meshInnerWallModel_CSG = CSG.fromMesh(meshInnerWallModel_Temp);
        const meshDoorDelModel_CSG = CSG.fromMesh(meshDoorDelModel_Temp);
 
        const innerWallModel_CSG = meshInnerWallModel_CSG.subtract(meshDoorDelModel_CSG);
        const CSGInnerWallModel = CSG.toMesh(
            innerWallModel_CSG,
            new THREE.Matrix4()
        )

        innerModel = CSGInnerWallModel.geometry;
    
        // outer wall model
        const verticalDepth = params.lean_p_w + cbFrontBackWallOffset * 2
        const horizontalDepth = params.lean_p_w + cbFrontBackWallOffset * 2
        const VSPosition = -params.lean_p_w / 2 - cbFrontBackWallOffset
        const HSPosition = params.lean_p_w / 2 + cbFrontBackWallOffset
    
        const verticalHorizontalPosS = 
            {
                "x": params.p_w / -2 - cbFrontBackWallOffset / 2,
                "y": (params.p_w_c_n && (params.p_b_c_b_l_b === 'Close') ? 2.935 : 0),
                "z": HSPosition
            }
        const verticalHorizontalRotS = 
            {
                "x": 0,
                "y": Math.PI,
                "z": Math.PI / 2
            }
        const patternLengthS = params.lean_p_h + params.b_l_t_r_p / 12 * params.lean_p_w / 2 + regularHeight + (params.p_w_c_n && (params.p_b_c_b_l_b === 'Close') ? -2.935 : 0)
        const patternDepthS = horizontalDepth
        
        const meshOuterWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLengthS, isPattern), extrudeSettings(patternDepthS)));
        const meshOuterWallDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(innerWallCutModel, extrudeSettings(1)))
        
        let meshOuterWallModel_Temp = meshOuterWallModel.clone();
        meshOuterWallModel_Temp.position.set(verticalHorizontalPosS.x,verticalHorizontalPosS.y,verticalHorizontalPosS.z);
        meshOuterWallModel_Temp.rotation.set(verticalHorizontalRotS.x,verticalHorizontalRotS.y,verticalHorizontalRotS.z);
        meshOuterWallModel_Temp.updateMatrixWorld();
    
    
        let meshOuterWallDelModel_Temp = meshOuterWallDelModel.clone();
        meshOuterWallDelModel_Temp.position.set(-params.p_w / 2 + 0.5, 0, 0);
        meshOuterWallDelModel_Temp.rotation.set(0,Math.PI / -2,0)
        meshOuterWallDelModel_Temp.updateMatrixWorld();
    
        const meshOuterWallModel_CSG = CSG.fromMesh(meshOuterWallModel_Temp);
        const meshOuterWallDelModel_CSG = CSG.fromMesh(meshOuterWallDelModel_Temp);

        const outerWallModel_CSG = meshOuterWallModel_CSG.subtract(meshDoorDelModel_CSG);
        const meshOuterWallDel_CSG = outerWallModel_CSG.intersect(meshOuterWallDelModel_CSG);
        const CSGOuterWallModel = CSG.toMesh(meshOuterWallDel_CSG,new THREE.Matrix4());

        outerModel = CSGOuterWallModel.geometry;

        // wainscot wall model
        const wainscotVerticalHorizontalPosS = 
            {
                "x": params.p_w / -2,
                "y" : 0,
                "z" : HSPosition
            }
        const wainscotVerticalHorizontalRotS = 
            {
                "x": 0,
                "y": Math.PI,
                "z": Math.PI / 2
            }
        const lengthPatternS = 3
        const depthPatternS = patternDepthS

        const meshWainscotWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPatternS, isPattern), extrudeSettings(depthPatternS)))
        let meshWainscotWallModel_Temp = meshWainscotWallModel.clone();
        meshWainscotWallModel_Temp.position.set(wainscotVerticalHorizontalPosS.x,wainscotVerticalHorizontalPosS.y,wainscotVerticalHorizontalPosS.z);
        meshWainscotWallModel_Temp.rotation.set(wainscotVerticalHorizontalRotS.x,wainscotVerticalHorizontalRotS.y,wainscotVerticalHorizontalRotS.z);
        meshWainscotWallModel_Temp.updateMatrixWorld();

        const meshWainscotWallModel_CSG = CSG.fromMesh(meshWainscotWallModel_Temp);

        const WainscotWallModel_CSG = meshWainscotWallModel_CSG.subtract(meshDoorDelModel_CSG);
        const WainscotWallModelCSG = WainscotWallModel_CSG.intersect(meshOuterWallDelModel_CSG);
        const CSGWainscotWallModel = CSG.toMesh(
            WainscotWallModelCSG,
            new THREE.Matrix4()
        )

        wainscotModel = CSGWainscotWallModel.geometry;

        return {innerModel, outerModel, wainscotModel}

    }, [params.wallTexture,params.lean_p_pos, params.p_v_w, params.p_w, params.b_l_t_r_p, params.lean_p_w, params.p_r_s, params.lean_p_d, params.lean_p_h, backWallCloseState, params.add_storage_check, params.add_storage, const_var.newDoorsArray,params.p_b_c_b_l_b, params.p_w_c_n, isPattern]);

    const GableLeftWallModelObj = useMemo(() => {
        if(params.p_b_c_b_l === 'Open') return;

        let outerModel;
        let innerModel;
        let wainscotModel;

        // inner wall model
        const innerWallModel = new THREE.Shape();

        if (params.p_b_c_b_l !== 'Open' || typeof(params.p_b_c_b_l) === 'object') {
            innerWallModel.moveTo(-params.lean_p_d + 0.005, params.lean_p_h);
            innerWallModel.lineTo(0, params.lean_p_h);
            innerWallModel.lineTo(0, leftWallCloseState + (params.p_r_s === 1 && 0.1));
            innerWallModel.lineTo(-params.lean_p_d + 0.005, leftWallCloseState + (params.p_r_s === 1 && 0.1));
            innerWallModel.closePath();
        }

        const innerWallPanelValue = (params.p_r_s === 1) ? leftWallCloseState + 0.1 : leftWallCloseState

        const innerWallCutModel = new THREE.Shape();
        innerWallCutModel.moveTo(-(params.p_w / 2), params.lean_p_h);                           //right lean left top
        innerWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_d), params.lean_p_h);         //right lean right top
        innerWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_d), innerWallPanelValue);     //right lean right bottom
        innerWallCutModel.lineTo(-(params.p_w / 2), innerWallPanelValue);                       //right lean left bottom
        innerWallCutModel.closePath()

        const storageLeftWallModel = new THREE.Shape();
        storageLeftWallModel.moveTo(-(params.p_w / 2), params.lean_p_h);                         
        storageLeftWallModel.lineTo(-(params.p_w / 2 + parseInt(params.add_storage)), params.lean_p_h);      
        storageLeftWallModel.lineTo(-(params.p_w / 2 + parseInt(params.add_storage)), 0); 
        storageLeftWallModel.lineTo(-(params.p_w / 2), 0);                    
        storageLeftWallModel.closePath();
        
        const meshInnerWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(innerWallModel, extrudeSettings(0.005)));
        const storageLeftWallDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(storageLeftWallModel, extrudeSettings(1)))
        const meshDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('gableLeftLeanLeft', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'red' }));
        const meshInnerWallDutchDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(dutchDelModelBack, extrudeSettings(1)), new THREE.MeshStandardMaterial({color: 'blue'}))

        let meshInnerWallModel_Temp = meshInnerWallModel.clone();
        meshInnerWallModel_Temp.position.set(-params.p_w / 2, 0, -params.lean_p_w / 2 - cbFrontBackWallOffset + 0.03);
        meshInnerWallModel_Temp.updateMatrixWorld();

        let storageLeftWallDelModel_Temp = storageLeftWallDelModel.clone();
        storageLeftWallDelModel_Temp.position.set(-params.leftLeanStoragePosition,0,params.lean_p_w / -2 - 0.5);
        storageLeftWallDelModel_Temp.updateMatrixWorld();
    
        let meshDoorDelModel_Temp = meshDoorDelModel.clone();
        meshDoorDelModel_Temp.position.set(0, 0, -params.lean_p_w / 2 - 0.5);
        meshDoorDelModel_Temp.updateMatrixWorld();

        let meshInnerWallDutchDelModel_Temp = meshInnerWallDutchDelModel.clone();
        meshInnerWallDutchDelModel_Temp.position.set(-(params.p_w / 2 + params.lean_p_d / 2), 0, - params.lean_p_w / 2 - 0.5);
        meshInnerWallDutchDelModel_Temp.updateMatrixWorld();

        const meshInnerWallModel_CSG = CSG.fromMesh(meshInnerWallModel_Temp);
        const storageLeftWallDelModel_CSG = CSG.fromMesh(storageLeftWallDelModel_Temp);
        const meshDoorDelModel_CSG = CSG.fromMesh(meshDoorDelModel_Temp);
        const meshInnerWallDutchDelModel_CSG = CSG.fromMesh(meshInnerWallDutchDelModel_Temp);

               
        let meshInnerStorageDelModel_CSG;
        if(params.add_storage_check){
            meshInnerStorageDelModel_CSG = meshInnerWallModel_CSG.subtract(storageLeftWallDelModel_CSG);
        } else{
            meshInnerStorageDelModel_CSG = meshInnerWallModel_CSG
        }

        const innerWallModel_CSG = meshInnerStorageDelModel_CSG.subtract(meshDoorDelModel_CSG);
        const meshInnerWallModelCSG = innerWallModel_CSG.subtract(meshInnerWallDutchDelModel_CSG);

        const CSGLeftWallModel = CSG.toMesh(
            meshInnerWallModelCSG,
            new THREE.Matrix4()
        )
    
        innerModel = CSGLeftWallModel.geometry;

        // outer wall model
        const verticalHorizontalPos = 
            {
                "x": params.p_w / -2,
                "y": params.p_w_c_n && params.p_b_c_b_l === 'Close' ? 2.935 : 0,
                "z": params.lean_p_w / -2 - 0.16
            }
        const verticalHorizontalRot = 
            {
                "x": 0,
                "y": Math.PI / -2,
                "z": Math.PI / 2
            }
        const patternLength = params.lean_p_h + (params.p_w_c_n && params.p_b_c_b_l === 'Close' ? -2.935 : 0)
        const patternDepth = params.lean_p_d

        const meshOuterWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength, isPattern), extrudeSettings(patternDepth)))  
        const meshOuterWallGableDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(innerWallCutModel, extrudeSettings(1)))
    
        let meshOuterWallGableDelModel_Temp = meshOuterWallGableDelModel.clone();
        meshOuterWallGableDelModel_Temp.position.set(0,0,params.lean_p_w / -2 - 0.5);
        meshOuterWallGableDelModel_Temp.updateMatrixWorld();
    
        let meshOuterWallModel_Temp = meshOuterWallModel.clone();
        meshOuterWallModel_Temp.position.set(verticalHorizontalPos.x,verticalHorizontalPos.y,verticalHorizontalPos.z);
        meshOuterWallModel_Temp.rotation.set(verticalHorizontalRot.x,verticalHorizontalRot.y,verticalHorizontalRot.z);
        meshOuterWallModel_Temp.updateMatrixWorld();
    
        const mesOuterhWallModel_CSG = CSG.fromMesh(meshOuterWallModel_Temp);
        const meshOuterGableDelModel_CSG = CSG.fromMesh(meshOuterWallGableDelModel_Temp);     
        
        let meshOuterStorageDelModel_CSG;
        if(params.add_storage_check){
            meshOuterStorageDelModel_CSG = mesOuterhWallModel_CSG.subtract(storageLeftWallDelModel_CSG);
        } else{
            meshOuterStorageDelModel_CSG = mesOuterhWallModel_CSG
        }
    
        const outerWallModel_CSG = meshOuterStorageDelModel_CSG.subtract(meshDoorDelModel_CSG);
        const outerWallGableModel_CSG = outerWallModel_CSG.intersect(meshOuterGableDelModel_CSG);
        const MeshtOuterWallModel_CSG = outerWallGableModel_CSG.subtract(meshInnerWallDutchDelModel_CSG);

        const CSGOuterWallModel = CSG.toMesh(MeshtOuterWallModel_CSG,new THREE.Matrix4());

        outerModel = CSGOuterWallModel.geometry;

        // wainscot wall model
        const wainscotVerticalHorizontalPos = 
            {
                "x": params.p_w / -2,
                "y": 0,
                "z": params.lean_p_w / -2 - 0.16
            }
        const wainscotVerticalHorizontalRot = 
            {
                "x": 0,
                "y": Math.PI / -2,
                "z": Math.PI / 2
            }
        const lengthPattern = 3
        const depthPattern = patternDepth

        const meshWainscotWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPattern, isPattern), extrudeSettings(depthPattern)))
        let meshWainscotWallModel_Temp = meshWainscotWallModel.clone();
        meshWainscotWallModel_Temp.position.set(wainscotVerticalHorizontalPos.x,wainscotVerticalHorizontalPos.y,wainscotVerticalHorizontalPos.z);
        meshWainscotWallModel_Temp.rotation.set(wainscotVerticalHorizontalRot.x,wainscotVerticalHorizontalRot.y,wainscotVerticalHorizontalRot.z);
        meshWainscotWallModel_Temp.updateMatrixWorld();

        const meshBackWainscotWallModel_CSG = CSG.fromMesh(meshWainscotWallModel_Temp);

        let meshWainscotStorageDelModel_CSG;
        if(params.add_storage_check){
            meshWainscotStorageDelModel_CSG = meshBackWainscotWallModel_CSG.subtract(storageLeftWallDelModel_CSG);
        } else{
            meshWainscotStorageDelModel_CSG = meshBackWainscotWallModel_CSG
        }

        const wainscotWallModel_CSG = meshWainscotStorageDelModel_CSG.subtract(meshDoorDelModel_CSG);
        const wainscotWallModelCSG = wainscotWallModel_CSG.subtract(meshInnerWallDutchDelModel_CSG);

        const CSGBackWainscotModel = CSG.toMesh(
            wainscotWallModelCSG,
            new THREE.Matrix4()
        )

        wainscotModel = CSGBackWainscotModel.geometry;

        return {innerModel, outerModel, wainscotModel}
    }, [params.wallTexture,rightWallCloseState, params.add_storage_check, params.leftLeanStoragePosition, params.add_storage, params.p_v_w, params.p_h, params.p_w, params.p_r_p, params.lean_p_pos, cbFrontBackWallOffset, params.p_b_c_b_l, params.p_r_s, params.lean_p_w, params.lean_p_d, params.b_l_t_r_p, params.lean_p_h, leftWallCloseState, const_var.newDoorsArray, params.p_w_c_n, isPattern]);

    const GableRightWallModelObj = useMemo(() => {
        if(params.p_b_c_b_l_r === 'Open') return;

        let outerModel;
        let innerModel;
        let wainscotModel;

        // inner wall model
        const innerWallModel = new THREE.Shape();

        if (params.p_b_c_b_l_r !== 'Open' || typeof(params.p_b_c_b_l_r) === 'object') {
            innerWallModel.moveTo(-params.lean_p_d + 0.005, params.lean_p_h);
            innerWallModel.lineTo(0, params.lean_p_h);
            innerWallModel.lineTo(0, rightWallCloseState + (params.p_r_s === 1 && 0.1));
            innerWallModel.lineTo(-params.lean_p_d + 0.005, rightWallCloseState + (params.p_r_s === 1 && 0.1));
            innerWallModel.closePath();
        };

        let innerWallPanelValue = (params.p_r_s === 1) ? rightWallCloseState + 0.1 : rightWallCloseState

        const innerWallCutModel = new THREE.Shape();

        innerWallCutModel.moveTo(-(params.p_w / 2), params.lean_p_h);                           //right lean left top
        innerWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_d), params.lean_p_h);         //right lean right top
        innerWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_d), innerWallPanelValue);     //right lean right bottom
        innerWallCutModel.lineTo(-(params.p_w / 2), innerWallPanelValue);                       //right lean left bottom
        innerWallCutModel.closePath();

        const storageRightWallModel = new THREE.Shape();
        storageRightWallModel.moveTo(-(params.p_w / 2), params.lean_p_h);                          
        storageRightWallModel.lineTo(-(params.p_w / 2 + parseInt(params.add_storage)), params.lean_p_h);        
        storageRightWallModel.lineTo(-(params.p_w / 2 + parseInt(params.add_storage)), 0);  
        storageRightWallModel.lineTo(-(params.p_w / 2), 0);                    
        storageRightWallModel.closePath();

        const meshInnerWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(innerWallModel, extrudeSettings(0.005)));
        const storageRightWallDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(storageRightWallModel, extrudeSettings(1)));
        const meshDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('gableLeftLeanRight', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'red' }));
        const meshInnerWallDutchDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(dutchDelModelFront, extrudeSettings(1)), new THREE.MeshStandardMaterial({color: 'blue'}))

        let meshInnerWallModel_Temp = meshInnerWallModel.clone();
        meshInnerWallModel_Temp.position.set(-params.p_w / 2, 0, params.lean_p_w / 2 + cbFrontBackWallOffset - 0.03);
        meshInnerWallModel_Temp.updateMatrixWorld();

        let storageRightWallDelModel_Temp = storageRightWallDelModel.clone();
        storageRightWallDelModel_Temp.position.set(-params.leftLeanStoragePosition, 0, params.lean_p_w / 2 + cbFrontBackWallOffset - 0.5);
        storageRightWallDelModel_Temp.updateMatrixWorld();
    
        let meshDoorDelModel_Temp = meshDoorDelModel.clone();
        meshDoorDelModel_Temp.position.set(0, 0, params.lean_p_w / 2 - 0.5);
        meshDoorDelModel_Temp.updateMatrixWorld();

        let meshInnerWallDutchDelModel_Temp = meshInnerWallDutchDelModel.clone();
        meshInnerWallDutchDelModel_Temp.position.set(-(params.p_w / 2 + params.lean_p_d / 2),0, params.lean_p_w / 2 - 0.5);
        meshInnerWallDutchDelModel_Temp.updateMatrixWorld();
    
        const meshInnerWallModel_CSG = CSG.fromMesh(meshInnerWallModel_Temp);
        const storageRightWallDelModel_CSG = CSG.fromMesh(storageRightWallDelModel_Temp);
        const meshDoorDelModel_CSG = CSG.fromMesh(meshDoorDelModel_Temp);
        const meshInnerWallDutchDelModel_CSG = CSG.fromMesh(meshInnerWallDutchDelModel_Temp);

        let meshInnerStorageDelModel_CSG;
        if(params.add_storage_check){
            meshInnerStorageDelModel_CSG = meshInnerWallModel_CSG.subtract(storageRightWallDelModel_CSG);
        } else{
            meshInnerStorageDelModel_CSG = meshInnerWallModel_CSG
        }
    
        const innerWallModel_CSG = meshInnerStorageDelModel_CSG.subtract(meshDoorDelModel_CSG);
        const innerWallModelCSG = innerWallModel_CSG.subtract(meshInnerWallDutchDelModel_CSG);

        const CSGInnerWallModel = CSG.toMesh(
            innerWallModelCSG,
            new THREE.Matrix4()
        )
    
        innerModel = CSGInnerWallModel.geometry;

        // outer wall model
        const patternLength = params.lean_p_h
        const patternDepth = params.lean_p_d
        const verticalHorizontalPos = 
            {
                "x": params.p_w / -2 - params.lean_p_d,
                "y": (params.p_w_c_n && (params.p_b_c_b_l_r === 'Close') ? 2.935 : 0),
                "z": params.lean_p_w / 2 + 0.16
            }
        const verticalHorizontalRot = 
            {
                "x": 0,
                "y": Math.PI / 2,
                "z": Math.PI / 2
            }
        const lengthPattern = 3;
        const depthPattern = patternDepth;

        const meshOuterWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength, isPattern), extrudeSettings(patternDepth)))  
        const meshOuterWallGableDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(innerWallCutModel, extrudeSettings(1)))
    
        let meshOuterWallGableDelModel_Temp = meshOuterWallGableDelModel.clone();
        meshOuterWallGableDelModel_Temp.position.set(0,0,params.lean_p_w / 2 - 0.5);
        meshOuterWallGableDelModel_Temp.updateMatrixWorld();
    
        let meshOuterWallModel_Temp = meshOuterWallModel.clone();
        meshOuterWallModel_Temp.position.set(verticalHorizontalPos.x,verticalHorizontalPos.y,verticalHorizontalPos.z);
        meshOuterWallModel_Temp.rotation.set(verticalHorizontalRot.x,verticalHorizontalRot.y,verticalHorizontalRot.z);
        meshOuterWallModel_Temp.updateMatrixWorld();
    
        const meshOuterWallModel_CSG = CSG.fromMesh(meshOuterWallModel_Temp);
        const meshOuterWallGableDelModel_CSG = CSG.fromMesh(meshOuterWallGableDelModel_Temp);

        let meshOuterStorageDelModel_CSG;
        if(params.add_storage_check){
            meshOuterStorageDelModel_CSG = meshOuterWallModel_CSG.subtract(storageRightWallDelModel_CSG);
        } else{
            meshOuterStorageDelModel_CSG = meshOuterWallModel_CSG
        }
    
        const outerWallModel_CSG = meshOuterStorageDelModel_CSG.subtract(meshDoorDelModel_CSG);
        const oueterWallGableModel_CSG = outerWallModel_CSG.intersect(meshOuterWallGableDelModel_CSG);
        const outerWallModelCSG = oueterWallGableModel_CSG.subtract(meshInnerWallDutchDelModel_CSG);

        const CSGWallModel = CSG.toMesh(outerWallModelCSG,new THREE.Matrix4());

        outerModel = CSGWallModel.geometry;
        
        // wainscot wall model
        const wainscotVerticalHorizontalPos = 
            {
                "x": params.p_w / -2 - params.lean_p_d,
                "y": 0,
                "z": params.lean_p_w / 2 + 0.16
            }
        const wainscotVerticalHorizontalRot = 
            {
                "x": 0,
                "y": Math.PI / 2,
                "z": Math.PI / 2
            }
        
        const meshWainscotWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPattern, isPattern), extrudeSettings(depthPattern)))

        let meshWainscotWallModel_Temp = meshWainscotWallModel.clone();
        meshWainscotWallModel_Temp.position.set(wainscotVerticalHorizontalPos.x,wainscotVerticalHorizontalPos.y,wainscotVerticalHorizontalPos.z);
        meshWainscotWallModel_Temp.rotation.set(wainscotVerticalHorizontalRot.x,wainscotVerticalHorizontalRot.y,wainscotVerticalHorizontalRot.z);
        meshWainscotWallModel_Temp.updateMatrixWorld();

        const meshWainscotWallModel_CSG = CSG.fromMesh(meshWainscotWallModel_Temp);

        let meshWainscotStorageDelModel_CSG;
        if(params.add_storage_check){
            meshWainscotStorageDelModel_CSG = meshWainscotWallModel_CSG.subtract(storageRightWallDelModel_CSG);
        } else{
            meshWainscotStorageDelModel_CSG = meshWainscotWallModel_CSG
        }

        const wainscotWallModel_CSG = meshWainscotStorageDelModel_CSG.subtract(meshDoorDelModel_CSG);
        const wainscotWallModelCSG = wainscotWallModel_CSG.subtract(meshInnerWallDutchDelModel_CSG);

        const CSGFrontWainscotModel = CSG.toMesh(
            wainscotWallModelCSG,
            new THREE.Matrix4()
        )

        wainscotModel = CSGFrontWainscotModel.geometry;

        return {outerModel, innerModel, wainscotModel};

    }, [params.wallTexture,params.p_h, params.add_storage_check, params.add_storage, params.leftLeanStoragePosition, params.p_w, params.p_r_p, params.lean_p_pos, params.p_w, params.lean_p_d, params.p_b_c_b_l_r, params.p_r_s, params.lean_p_w, params.lean_p_h, params.b_l_t_r_p, rightWallCloseState, const_var.newDoorsArray, params.p_w_c_n, params.p_v_w, isPattern]);    

    const texture = useMemo(() => {
        if(const_var.metalTexture){
            let texture = const_var.metalTexture.clone();
            textureAnisotropy(gl, texture,1.3, 1.3, (params.p_v_w != true) ? 0 :  Math.PI/-2);    
            return texture
        }else{
            return null
        }
    }, [params.p_v_w,params.wallTexture,const_var.wallTextureObj[params.wallTexture]])

    // --- left wall close state    
    useMemo(() => {
        let tempWallCloseSize = 0;
        if (params.p_b_c_b_l === 'Open' || typeof(params.p_b_c_b_l) === 'object') {
            if (params.p_r_s === 1) {
                tempWallCloseSize = params.lean_p_h - 0.25;
            } else {
                tempWallCloseSize = params.lean_p_h;
            }
        } else if (params.p_b_c_b_l === "One_Fourth_Close") {
            tempWallCloseSize = params.lean_p_h / 4 * 3;
        } else if (params.p_b_c_b_l === "Half_Close") {
            tempWallCloseSize = params.lean_p_h / 2;
        } else if (params.p_b_c_b_l === "Three_Fourth_Close") {
            tempWallCloseSize = params.lean_p_h / 4;
        } else if (params.p_b_c_b_l === "Close") {
            tempWallCloseSize = 0;
        } else {
            tempWallCloseSize = params.lean_p_h - 3 * params.p_b_c_b_l;
        }

        setLeftWallCloseState(tempWallCloseSize);
    }, [params.p_b_c_b_l, params.lean_p_h, params.p_r_s])

    // --- right wall set    
    useMemo(() => {
        let tempWallCloseSize = 0;
        if (params.p_b_c_b_l_r === 'Open' || typeof(params.p_b_c_b_l_r) === 'object') {
            if (params.p_r_s === 1) {
                tempWallCloseSize = params.lean_p_h - 0.25;
            } else {
                tempWallCloseSize = params.lean_p_h;
            }
        } else if (params.p_b_c_b_l_r === "One_Fourth_Close") {
            tempWallCloseSize = params.lean_p_h / 4 * 3;
        } else if (params.p_b_c_b_l_r === "Half_Close") {
            tempWallCloseSize = params.lean_p_h / 2;
        } else if (params.p_b_c_b_l_r === "Three_Fourth_Close") {
            tempWallCloseSize = params.lean_p_h / 4;
        } else if (params.p_b_c_b_l_r === "Close") {
            tempWallCloseSize = 0;
        } else {
            tempWallCloseSize = params.lean_p_h - 3 * params.p_b_c_b_l_r;
        }

        setRightWallCloseState(tempWallCloseSize);
    }, [params.p_b_c_b_l_r, params.lean_p_h, params.p_r_s])

    // --- front wall close state    
    useMemo(() => {
        let tempWallCloseSize = 0;
        if (params.p_b_c_b_l_f === "Gable") {
            if (params.p_r_s === 1) {
                tempWallCloseSize = params.lean_p_h - 0.25;
            } else {
                tempWallCloseSize = params.lean_p_h - 0.2;
            }
        } else if (params.p_b_c_b_l_f === "One_Fourth_Close") {
            tempWallCloseSize = params.lean_p_h / 4 * 3;
        } else if (params.p_b_c_b_l_f === "Half_Close") {
            tempWallCloseSize = params.lean_p_h / 2;
        } else if (params.p_b_c_b_l_f === "Three_Fourth_Close") {
            tempWallCloseSize = params.lean_p_h / 4;
        } else if (params.p_b_c_b_l_f === "Half Dutch End") {
            tempWallCloseSize = 0;
        } else if (params.p_b_c_b_l_f === "Half End") {
            tempWallCloseSize = 0;
        } else if (params.p_b_c_b_l_f === "Close") {
            tempWallCloseSize = 0;
        } else {
            tempWallCloseSize = -1;
        }

        // if(params.p_w_c_n && params.p_b_c_b_l_f === "Close") tempWallCloseSize = 3;

        setFrontWallCloseState(tempWallCloseSize);
    }, [params.p_b_c_b_l_f, params.lean_p_h, params.p_r_s, params.p_w_c_n])

    // --- back wall close state
    useMemo(() => {
        let tempWallCloseSize;
        if (params.p_b_c_b_l_b === "Gable") {
            if (params.p_r_s === 1) {
                tempWallCloseSize = params.lean_p_h - 0.25;
            } else {
                tempWallCloseSize = params.lean_p_h - 0.2;
            }
        } else if (params.p_b_c_b_l_b === "One_Fourth_Close") {
            tempWallCloseSize = params.lean_p_h / 4 * 3;
        } else if (params.p_b_c_b_l_b === "Half_Close") {
            tempWallCloseSize = params.lean_p_h / 2;
        } else if (params.p_b_c_b_l_b === "Three_Fourth_Close") {
            tempWallCloseSize = params.lean_p_h / 4;
        } else if (params.p_b_c_b_l_b === "Half Dutch End") {
            tempWallCloseSize = 0;
        } else if (params.p_b_c_b_l_b === "Half End") {
            tempWallCloseSize = 0;
        } else if (params.p_b_c_b_l_b === "Close") {
            tempWallCloseSize = 0;
        } else {
            tempWallCloseSize = -1;
        }

        // if(params.p_w_c_n && params.p_b_c_b_l_b === "Close") tempWallCloseSize = 3;

        setBackWallCloseState(tempWallCloseSize);
    }, [params.p_b_c_b_l_b, params.lean_p_h, params.p_r_s, params.p_w_c_n])

    const handleMouseEvent = (e) => {
        if (controls.isCheckActiveTab === 'doorwindow' && (const_var.focusedSection === "leftlean" && params.activeWall === "L_L_F_S_W")) {
            switch (e.type) {
                case 'pointerdown':
                case 'click':
                case 'pointermove':
                    e.stopPropagation();
                    break;
                default :
                    break
            }
        }
    };
    const eventHandlers = useMouseEvents(handleMouseEvent);
    
    return (
        <>
            <group name='front_wall' {...eventHandlers}>
                {params.p_b_c_b_l_f !== 'Open' && GableFrontWallModelObj?.outerModel &&
                    <mesh name="G_L_L_F_W_outer" geometry={GableFrontWallModelObj?.outerModel} renderOrder={1}>
                        <MaterialOuterWallNew isPattern={isPattern}   visibleSection={"gableLeftLean"} />
                    </mesh>
                }
                {params.p_w_c_n === true && params.p_b_c_b_l_f === "Close" && GableFrontWallModelObj?.wainscotModel &&
                    <mesh name="G_L_L_F_W_wainscot" geometry={GableFrontWallModelObj?.wainscotModel} position={[-0.005,0,0]} renderOrder={1}>
                        <MaterialWainScotWallNew isPattern={isPattern}   visibleSection={"gableLeftLean"} />
                    </mesh>
                }
                {params.p_b_c_b_l_f !== 'Open' && GableFrontWallModelObj && GableFrontWallModelObj?.innerModel && 
                    <mesh name="G_L_L_F_W_inner" geometry={GableFrontWallModelObj?.innerModel} position={[wallThickness + 0.01, 0, 0]}>
                        <MaterialInnerWall texture={texture} visibleSection={"gableLeftLean"} />
                    </mesh>
                }
            </group>
            {params.p_l_w !== 'Close' &&
                <group name='back_wall'>
                    {params.p_b_c_b_l_b !== 'Open' && GableBackWallModelObj?.outerModel &&
                        <mesh name="G_L_L_B_W_outer" geometry={GableBackWallModelObj?.outerModel} renderOrder={1}>
                            <MaterialOuterWallNew isPattern={isPattern}   visibleSection={"gableLeftLean"} />
                        </mesh>
                    }
                    {params.p_w_c_n === true && params.p_b_c_b_l_b === "Close" && GableBackWallModelObj?.wainscotModel &&
                        <mesh name="G_L_L_B_W_wainscot" geometry={GableBackWallModelObj?.wainscotModel} position={[-0.005,0,0]} renderOrder={1}>
                            <MaterialWainScotWallNew isPattern={isPattern}   visibleSection={"gableLeftLean"} />
                        </mesh>
                    }
                    {params.p_b_c_b_l_b !== "Open" && GableBackWallModelObj && GableBackWallModelObj?.innerModel && 
                        <mesh name="G_L_L_B_W_inner" geometry={GableBackWallModelObj?.innerModel} position={[wallThickness + 0.01, 0, 0]}>
                            <MaterialInnerWall texture={texture} visibleSection={"gableLeftLean"} />
                        </mesh>
                    }
                </group>
            }
            <group name='right-wall'>
                {params.p_b_c_b_l_r !== 'Open' && GableRightWallModelObj?.outerModel &&
                    <mesh name="G_L_L_R_W_outer" geometry={GableRightWallModelObj?.outerModel} renderOrder={1}>
                        <MaterialOuterWallNew isPattern={isPattern}   visibleSection={"gableLeftLean"} />
                    </mesh>
                }
                {params.p_w_c_n === true && (params.p_b_c_b_l_r === "Close" || params.p_b_c_b_l_r === "Half Dutch End" || params.p_b_c_b_l_r === "Half End") && GableRightWallModelObj?.wainscotModel &&
                    <mesh name="G_L_L_R_W_wainscot" geometry={GableRightWallModelObj?.wainscotModel} position={[0,0,0.005]} renderOrder={1}>
                        <MaterialWainScotWallNew isPattern={isPattern}   visibleSection={"gableLeftLean"} />
                    </mesh>
                }
                {GableRightWallModelObj && GableRightWallModelObj?.innerModel &&
                    <mesh name="G_L_L_R_W_inner" geometry={GableRightWallModelObj?.innerModel} position={[0, 0, -wallThickness * 3 - 0.01]}>
                        <MaterialInnerWall texture={texture} visibleSection={"gableLeftLean"} />
                    </mesh>
                }
            </group>
            <group name='left-wall'>
                {params.p_b_c_b_l !== 'Open' && GableLeftWallModelObj?.outerModel &&
                    <mesh name="G_L_L_L_W_outer" geometry={GableLeftWallModelObj?.outerModel} renderOrder={1}>
                        <MaterialOuterWallNew isPattern={isPattern}   visibleSection={"gableLeftLean"} />
                    </mesh>
                }
                {params.p_w_c_n === true && (params.p_b_c_b_l === "Close" || params.p_b_c_b_l === "Half Dutch End" || params.p_b_c_b_l === "Half End") && GableLeftWallModelObj?.wainscotModel &&
                    <mesh name="G_L_L_L_W_wainscot" geometry={GableLeftWallModelObj?.wainscotModel} position={[0,0,-0.005]} renderOrder={1}>
                        <MaterialWainScotWallNew isPattern={isPattern}   visibleSection={"gableLeftLean"} />
                    </mesh>
                }
                {GableLeftWallModelObj && GableLeftWallModelObj?.innerModel &&
                    <mesh name="G_L_L_L_W_inner" geometry={GableLeftWallModelObj?.innerModel} position={[0, 0, wallThickness + 0.01]}>
                        <MaterialInnerWall texture={texture} visibleSection={"gableLeftLean"} />
                    </mesh>
                }
            </group>
        </>
    )
}

export default WallModel